// Toast - Replacement for alert()

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { Icons } from '../../icons';
import './Toast.css';

type ToastVariant = 'success' | 'error' | 'warning' | 'info';

interface ToastOptions {
  message: string;
  variant?: ToastVariant;
  duration?: number;
}

interface ToastItem extends ToastOptions {
  id: string;
}

interface ToastContextType {
  toast: (options: ToastOptions | string) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  warning: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback((options: ToastOptions | string) => {
    const toastOptions: ToastOptions = typeof options === 'string'
      ? { message: options, variant: 'info' }
      : options;

    const id = `toast-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    const duration = toastOptions.duration ?? 4000;

    setToasts((prev) => [...prev, { ...toastOptions, id }]);

    if (duration > 0) {
      setTimeout(() => removeToast(id), duration);
    }
  }, [removeToast]);

  const toast = useCallback((options: ToastOptions | string) => addToast(options), [addToast]);
  const success = useCallback((message: string) => addToast({ message, variant: 'success' }), [addToast]);
  const error = useCallback((message: string) => addToast({ message, variant: 'error', duration: 6000 }), [addToast]);
  const warning = useCallback((message: string) => addToast({ message, variant: 'warning' }), [addToast]);
  const info = useCallback((message: string) => addToast({ message, variant: 'info' }), [addToast]);

  const getIcon = (variant: ToastVariant = 'info') => {
    switch (variant) {
      case 'success': return <Icons.Check />;
      case 'error': return <Icons.AlertTriangle />;
      case 'warning': return <Icons.AlertTriangle />;
      case 'info': return <Icons.AlertCircle />;
    }
  };

  return (
    <ToastContext.Provider value={{ toast, success, error, warning, info }}>
      {children}
      {toasts.length > 0 && (
        <div className="toast-container">
          {toasts.map((t) => (
            <div key={t.id} className={`toast toast--${t.variant || 'info'}`}>
              <span className="toast__icon">{getIcon(t.variant)}</span>
              <span className="toast__message">{t.message}</span>
              <button className="toast__close" onClick={() => removeToast(t.id)}>
                <Icons.X />
              </button>
            </div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useToast(): ToastContextType {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
